import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { RecentMailWarning } from '@/components/postgrid/common/RecentMailWarning';
import { ContactComboboxWithCreate } from '@/components/postgrid/contact-combobox/ContactComboboxWithCreate';
import { WizardStep } from '@/components/postgrid/wizard';
import { Checkbox } from '@/components/ui/catalyst/checkbox';
import { useMatter } from '@/hooks/useMatter';
import { enhancedApi } from '@/services/api/generated/postgridService';
import type { ExtractedRecipientInfo } from '@/services/api/postgridService';

const contactsStepSchema = z.object({
  to_contact_id: z.string().min(1, 'Recipient contact is required'),
  use_return_envelope: z.boolean().default(false),
});

type ContactsStepData = z.infer<typeof contactsStepSchema>;

interface LetterContactsStepProps {
  wizardData?: {
    to_contact_id?: string;
    to_contact_name?: string;
    use_return_envelope?: boolean;
  };
  updateStepData?: (data: Record<string, unknown>) => void;
  markStepComplete?: (isValid: boolean) => void;
  /** Extracted recipient info from uploaded document - used to pre-populate contact creation */
  extractedRecipient?: ExtractedRecipientInfo | null;
}

export const LetterContactsStep: React.FC<LetterContactsStepProps> = ({
  wizardData = {},
  updateStepData,
  markStepComplete,
  extractedRecipient,
}) => {
  const { matterId } = useMatter();
  const {
    control,
    watch,
    formState: { errors, isValid },
  } = useForm<ContactsStepData>({
    resolver: zodResolver(contactsStepSchema),
    defaultValues: {
      to_contact_id: wizardData.to_contact_id || '',
      use_return_envelope: wizardData.use_return_envelope || false,
    },
    mode: 'onChange',
  });

  const watchedToContact = watch('to_contact_id');
  const watchedUseReturnEnvelope = watch('use_return_envelope');

  // Fetch contact details - load all contacts immediately
  const { data: contactsResponse } = enhancedApi.usePostgridContactsListQuery(
    { q: '', isActive: true, limit: 100 },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const contactsList = contactsResponse?.results || [];

  // Find the selected contact from the list
  const contactDetails = contactsList?.find(
    (contact) => contact.id === watchedToContact,
  );

  // Update wizard data when form values change
  useEffect(() => {
    if (updateStepData) {
      updateStepData({
        to_contact_id: watchedToContact,
        use_return_envelope: watchedUseReturnEnvelope,
      });
    }
  }, [watchedToContact, watchedUseReturnEnvelope, updateStepData]);

  // Mark step as complete/incomplete based on validation
  useEffect(() => {
    markStepComplete?.(isValid && Boolean(watchedToContact));
  }, [isValid, watchedToContact, markStepComplete]);

  const handleToContactChange = (contactId: string | null) => {
    // Update step data with contact selection
    if (updateStepData) {
      const selectedContact = contactsList?.find(
        (contact) => contact.id === contactId,
      );

      const firstName = selectedContact?.first_name;
      const companyName = selectedContact?.company_name;
      const fallbackFirstName = contactDetails?.first_name;
      const fallbackCompanyName = contactDetails?.company_name;

      updateStepData({
        to_contact_id: contactId || '',
        to_contact_name:
          firstName ||
          companyName ||
          fallbackFirstName ||
          fallbackCompanyName ||
          'Selected Contact',
      });
    }
  };

  return (
    <WizardStep
      title="Letter Recipient"
      description="Select who this letter is being sent to."
    >
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h4 className="text-sm font-medium text-gray-900 mb-4">
          Recipient Details
        </h4>

        <div className="space-y-4">
          {/* Recipient Selection */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-shrink-0 w-24">
              <span className="text-sm text-gray-600">Recipient *</span>
            </div>
            <div className="flex-1 min-w-0">
              <Controller
                name="to_contact_id"
                control={control}
                render={({ field }) => (
                  <ContactComboboxWithCreate
                    value={field.value}
                    onChange={(contactId, contactName) => {
                      field.onChange(contactId);
                      handleToContactChange(contactId);
                      // Update step data with contact name if provided
                      if (contactName && updateStepData) {
                        updateStepData({
                          to_contact_id: contactId || '',
                          to_contact_name: contactName,
                        });
                      }
                    }}
                    placeholder="Select recipient..."
                    aria-label="Select recipient contact"
                    className="w-full"
                    extractedRecipient={extractedRecipient}
                  />
                )}
              />
              {errors.to_contact_id && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.to_contact_id.message}
                </p>
              )}
            </div>
          </div>

          {/* Recent Mail Warning */}
          {watchedToContact && matterId && (
            <RecentMailWarning
              matterId={matterId}
              contactId={watchedToContact}
              lookbackDays={30}
            />
          )}

          {/* Return Envelope Option */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-shrink-0 w-24">
              <span className="text-sm text-gray-600">Options</span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Controller
                  name="use_return_envelope"
                  control={control}
                  render={({ field }) => (
                    <Checkbox checked={field.value} onChange={field.onChange} />
                  )}
                />
                <span className="text-sm text-gray-700">
                  Include return envelope
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </WizardStep>
  );
};
